import { useState, type ReactNode } from 'react'
import { type LucideIcon, ArrowLeft, ArrowRightLeft, Activity, AlarmClock, BarChart3, BookOpen, Bot, Building2, Calendar, CalendarCheck, CalendarClock, CalendarDays, CalendarOff, CheckSquare, ChevronDown, ClipboardCheck, ClipboardList, CreditCard, DatabaseZap, FileClock, FileText, Folder, FolderOpen, Globe, Handshake, HelpCircle, History, Home, Key, LifeBuoy, Mail, MapPin, Megaphone, PenLine, Settings, Shield, ShieldCheck, Store, User, Users, Video, Webhook, Share2, Inbox } from 'lucide-react'
import { NavLink } from 'react-router'
import { usePermissions } from '../../hooks/usePermissions'
import { useEnabledFeatures } from '../../hooks/useEnabledFeatures'
import { Tooltip } from '../ui'

interface SidebarProps {
  collapsed: boolean
  mobileOpen?: boolean
  onToggle: () => void
  /** 'admin' sostituisce brand/sezioni/footer con la variante Pannello Super Admin, riusando lo stesso guscio invece di duplicare il markup (vedi AdminLayout). */
  variant?: 'app' | 'admin'
}

interface SidebarItem {
  to: string
  icon: LucideIcon
  label: string
  /** true per una route che ha figlie (es. /agency ha /agency/settings): senza,
   * NavLink la marca attiva anche quando la route attiva è in realtà una figlia. */
  end?: boolean
  /** Permesso granulare richiesto per vedere la voce: senza, un utente senza
   * accesso (es. un Responsabile su Chiavi API/Webhook, riservati al backend
   * al solo Titolare) vede comunque il link e ottiene un errore di
   * caricamento generico invece di non vedere affatto la voce. */
  requiresPermission?: string
  /** Feature flag di agenzia richiesto (es. 'VirtualTours'): a flag spento la
   * voce è nascosta. Il controllo è fail-open (vedi useEnabledFeatures). */
  requiresFeature?: string
}

interface SidebarSection {
  title: string
  items: SidebarItem[]
}

/** Titoli delle sezioni collassate, persistiti così la preferenza sopravvive al reload.
 * Si memorizzano i CHIUSI (non gli aperti): le nuove sezioni future nascono aperte. */
const COLLAPSED_SECTIONS_KEY = 'domusaurea:sidebar-collapsed-sections'

function loadCollapsedSections(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_SECTIONS_KEY)
    if (!raw) return new Set()
    const parsed: unknown = JSON.parse(raw)
    return new Set(Array.isArray(parsed) ? parsed.filter((t): t is string => typeof t === 'string') : [])
  } catch {
    return new Set()
  }
}

const adminSections: SidebarSection[] = [
  {
    title: 'Multi-tenant',
    items: [
      { to: '/admin/agencies', icon: Building2, label: 'Agenzie' },
      { to: '/admin/users', icon: Users, label: 'Utenti' },
      { to: '/admin/omi-import', icon: DatabaseZap, label: 'Import OMI' },
    ],
  },
  {
    title: 'Comunicazioni',
    items: [{ to: '/admin/announcements', icon: Megaphone, label: 'Annunci Globali' }],
  },
  {
    title: 'Contenuti',
    items: [{ to: '/admin/content-categories', icon: FolderOpen, label: 'Categorie KB/FAQ' }],
  },
  {
    title: 'Sistema',
    items: [
      { to: '/admin/system-logs', icon: Shield, label: 'Log di Sistema' },
      { to: '/admin/changelog', icon: FileClock, label: 'Changelog' },
    ],
  },
]

const sections: SidebarSection[] = [
  {
    title: 'Pianificazione',
    items: [
      { to: '/planner', icon: Calendar, label: 'Agenda' },
      { to: '/planner/today', icon: CalendarClock, label: 'Oggi' },
      { to: '/visits', icon: CalendarCheck, label: 'Visite' },
      { to: '/todos', icon: CheckSquare, label: 'ToDo' },
      { to: '/deadlines', icon: AlarmClock, label: 'Scadenzario' },
      { to: '/timeline', icon: History, label: 'Timeline' },
    ],
  },
  {
    title: 'Gestione',
    items: [
      { to: '/customers', icon: Users, label: 'Clienti' },
      { to: '/lead', icon: Inbox, label: 'Inbox lead', requiresPermission: 'Permissions.Customers.View' },
      { to: '/properties', icon: Building2, label: 'Immobili' },
      { to: '/property-keys', icon: Key, label: 'Chiavi', requiresPermission: 'Permissions.Properties.View' },
      { to: '/property-handoffs', icon: ArrowRightLeft, label: 'Passati a me' },
      { to: '/property-owners', icon: Home, label: 'Proprietari' },
      { to: '/assignments', icon: ClipboardCheck, label: 'Incarichi' },
      { to: '/offer-practices', icon: ClipboardList, label: 'Pratiche aperte', requiresPermission: 'Permissions.Offers.View' },
      { to: '/invoices', icon: FileText, label: 'Fatture', requiresPermission: 'Permissions.Invoices.View' },
      { to: '/signatures', icon: PenLine, label: 'Firme', requiresPermission: 'Permissions.Signatures.View' },
      { to: '/leases', icon: Home, label: 'Locazioni', requiresPermission: 'Permissions.Leases.View' },
      { to: '/requests', icon: FileText, label: 'Richieste' },
      { to: '/selections', icon: Share2, label: 'Selezioni cliente' },
      { to: '/compliance', icon: ShieldCheck, label: 'Compliance', requiresPermission: 'Permissions.Customers.View' },
    ],
  },
  {
    title: 'Comunicazioni',
    items: [
      { to: '/news', icon: Megaphone, label: 'News' },
      { to: '/announcements', icon: Megaphone, label: 'Comunicazioni' },
      { to: '/email-templates', icon: FileText, label: 'Template Email' },
    ],
  },
  {
    title: 'Strumenti',
    items: [
      { to: '/tools/calendar-settings', icon: CalendarDays, label: 'Visibilità calendari' },
      { to: '/portals', icon: Globe, label: 'Portali' },
      { to: '/virtual-tours', icon: Video, label: 'Virtual Tour 360', requiresFeature: 'VirtualTours' },
      { to: '/emails', icon: Mail, label: 'Email' },
      { to: '/partners', icon: Handshake, label: 'Partners' },
      { to: '/map', icon: MapPin, label: 'Mappa' },
    ],
  },
  {
    title: 'Integrazioni',
    items: [
      { to: '/integrations/api-keys', icon: Key, label: 'Chiavi API', requiresPermission: 'Permissions.Agency.Settings' },
      { to: '/integrations/webhooks', icon: Webhook, label: 'Webhook', requiresPermission: 'Permissions.Agency.Settings' },
      { to: '/integrations/ai-assistants', icon: Bot, label: 'Assistenti AI', requiresPermission: 'Permissions.Agency.Settings' },
      { to: '/integrations/agency-website', icon: Globe, label: 'Sito agenzia', requiresPermission: 'Permissions.Agency.Settings' },
      { to: '/integrations/api-docs', icon: BookOpen, label: 'Documentazione API' },
      { to: '/integrations/google-calendar', icon: Calendar, label: 'Google Calendar' },
    ],
  },
  {
    title: 'Agenzia',
    items: [
      { to: '/agency', icon: Settings, label: 'Dashboard', end: true },
      { to: '/employees', icon: Users, label: 'Dipendenti' },
      { to: '/employee-absences', icon: CalendarOff, label: 'Copertura' },
      { to: '/branches', icon: Store, label: 'Filiali' },
      { to: '/subscription', icon: CreditCard, label: 'Abbonamento' },
      { to: '/activity', icon: Activity, label: 'Attività' },
      { to: '/reports', icon: BarChart3, label: 'Report' },
      { to: '/system/changelog', icon: FileClock, label: 'Changelog' },
    ],
  },
  {
    title: 'Supporto',
    items: [
      { to: '/guida', icon: LifeBuoy, label: 'Guida' },
      { to: '/files', icon: Folder, label: 'File Manager' },
      { to: '/documents', icon: FolderOpen, label: 'Documenti' },
      { to: '/faq', icon: HelpCircle, label: 'FAQ' },
      { to: '/knowledge-base', icon: BookOpen, label: 'Knowledge Base' },
    ],
  },
]

export function Sidebar({ collapsed, mobileOpen, variant = 'app' }: SidebarProps) {
  const classes = ['sidebar']
  if (collapsed) classes.push('collapsed')
  if (mobileOpen) classes.push('show')
  const isAdmin = variant === 'admin'
  const { can } = usePermissions()
  const { isEnabled } = useEnabledFeatures()
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(loadCollapsedSections)
  const toggleSection = (title: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(title)) next.delete(title)
      else next.add(title)
      try {
        localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify([...next]))
      } catch {
        // storage non disponibile (es. navigazione privata): la preferenza vale per la sessione
      }
      return next
    })
  }
  const visibleSections = (isAdmin ? adminSections : sections)
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          (!item.requiresPermission || can(item.requiresPermission)) &&
          (!item.requiresFeature || isEnabled(item.requiresFeature)),
      ),
    }))
    .filter((section) => section.items.length > 0)
  // Da collassata le label sono nascoste e il tooltip CSS (::after) verrebbe tagliato
  // dall'overflow della sidebar-nav: si usa il componente Tooltip (portal + flip automatico).
  const maybeTooltip = (label: string, link: ReactNode) =>
    collapsed ? (
      <Tooltip content={label} position="right">
        {link}
      </Tooltip>
    ) : (
      link
    )

  return (
    <aside className={classes.join(' ')} id="sidebar">
      <div className="sidebar-header">
        <a className="sidebar-brand" href={isAdmin ? '/admin' : '/'}>
          <img src="/apple-touch-icon.png" alt="" className="h-6 w-6 shrink-0 rounded-md" />
          <span className="sidebar-brand-text shrink-0">DomusAurea</span>
          {isAdmin && (
            <span className="sidebar-brand-badge ml-1.5 shrink-0 rounded-full bg-primary-500/20 px-1.5 py-0.5 text-xs font-semibold text-primary-300">
              Admin
            </span>
          )}
        </a>
      </div>

      <nav className="sidebar-nav">
        <ul className="sidebar-menu">
          {!isAdmin && (
            <li className="sidebar-item">
              {maybeTooltip(
                'Home',
                <NavLink className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`} to="/" end data-title="Home">
                  <Home size={18} />
                  <span className="sidebar-text">Home</span>
                </NavLink>
              )}
            </li>
          )}

          {visibleSections.map((section) => {
            const isOpen = !collapsedSections.has(section.title)
            return (
            <li key={section.title}>
              <button
                type="button"
                className="sidebar-section sidebar-section-toggle"
                aria-expanded={isOpen}
                aria-label={`${isOpen ? 'Comprimi' : 'Espandi'} sezione ${section.title}`}
                onClick={() => toggleSection(section.title)}
              >
                <span className="sidebar-section-text">{section.title}</span>
                <div className="sidebar-section-line"></div>
                <ChevronDown size={14} className={`sidebar-section-chevron${isOpen ? ' open' : ''}`} aria-hidden="true" />
              </button>
              {isOpen && (
              <ul className="sidebar-menu">
                {section.items.map((item) => {
                  const Icon = item.icon
                  return (
                    <li className="sidebar-item" key={item.to}>
                      {maybeTooltip(
                        item.label,
                        <NavLink className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`} to={item.to} end={item.end} data-title={item.label}>
                          <Icon size={18} />
                          <span className="sidebar-text">{item.label}</span>
                        </NavLink>
                      )}
                    </li>
                  )
                })}
              </ul>
              )}
            </li>
            )
          })}
        </ul>
      </nav>

      {isAdmin && (
        <div className="sidebar-footer">
          {maybeTooltip(
            "Torna all'App",
            <NavLink className="sidebar-link" to="/" data-title="Torna all'App">
              <ArrowLeft size={18} />
              <span className="sidebar-text">Torna all'App</span>
            </NavLink>
          )}
        </div>
      )}
    </aside>
  )
}
